import classNames from 'classnames';
import { useFormik } from 'formik';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import {
  initialValuesAssociationDetails,
  validationSchemaAssociationDetails,
} from '@/modules/entity/components/association/AssociationDetails.tsx';
import { CompanyDetailsShareholder } from '@/modules/entity/components/company/CompanyDetails.tsx';
import { countryOptions } from '@/modules/entity/static/countries.ts';
import { AssociationEntityDetails } from '@/modules/entity/types/EntityItemResponse.ts';
import { OnboardingHolderWrapper } from '@/modules/issuer-dashboard/components/holder-create/OnboardingHolderWrapper.tsx';
import { OnboardingStepProps } from '@/modules/issuer-dashboard/components/holder-create/roadsTypes.tsx';
import {
  useIssuerGetInvestorEditByIdQuery,
  useUpdateInvestorEditMutation,
} from '@/modules/issuer-dashboard/queries/holder-create.tsx';
import { Button, CustomCheckbox, Select, SelectOption, TextInputField } from 'ui';
import { useUnsavedChangesStore } from 'ui/hooks';
import { PlusBlueIcon, TrashIcon } from 'ui/icons';
import { UpdateEntityRequest } from 'api';

export const AssociationDetailsHolder = ({ moveForward, allowSkip }: OnboardingStepProps) => {
  const { id: entity_id = 0 } = useParams();
  const { data: dataHolder } = useIssuerGetInvestorEditByIdQuery(
    entity_id ? +entity_id : -1,
    !entity_id || +entity_id === 0 || isNaN(+entity_id),
  );

  const getInitialValues = (): AssociationEntityDetails => {
    const entityDetails = dataHolder?.data?.new_data?.entity_data?.association_entity_details_id;
    return {
      ...initialValuesAssociationDetails,
      country:
        dataHolder?.data?.new_data?.entity_data?.country ||
        initialValuesAssociationDetails?.country,
      ...entityDetails,
      shareholders:
        (entityDetails?.shareholders || [])?.map(item => ({
          ...item,
          type: item.company_name ? 'company' : 'individual',
          index: item.index || uuidv4(),
        })) || initialValuesAssociationDetails?.shareholders,
      officers:
        (entityDetails?.officers || [])?.map(item => ({
          ...item,
          type: item.company_name ? 'company' : 'individual',
          index: item.index || uuidv4(),
        })) || initialValuesAssociationDetails?.officers,
    };
  };

  const formik = useFormik<AssociationEntityDetails>({
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema: validationSchemaAssociationDetails,
    initialValues: getInitialValues(),
    enableReinitialize: true,
    onSubmit: async ({ company_name, ...details }) => {
      setDirty(false);
      if (formik.dirty) {
        await updateEntity({
          id: dataHolder?.data?.id,
          user_data: dataHolder?.data?.new_data?.user_data,
          dividend_reinvestment: dataHolder?.data?.new_data?.dividend_reinvestment,
          entity_data: {
            ...dataHolder?.data?.new_data?.entity_data,
            entity_name: company_name,
            association_entity_details_id: {
              ...dataHolder?.data?.new_data?.entity_data?.association_entity_details_id,
              ...details,
              company_name,
            },
          } as UpdateEntityRequest,
        });
      }
      moveForward && moveForward();
    },
  });

  const [dirty, setDirty] = useState(formik.dirty);
  const setUnsavedChanges = useUnsavedChangesStore(state => state.setUnsavedChanges);
  setUnsavedChanges(dirty);
  useEffect(() => {
    setDirty(formik.dirty);
  }, [formik.dirty]);

  const { mutateAsync: updateEntity, isLoading } = useUpdateInvestorEditMutation({
    setErrors: formik.setErrors,
  });

  const changeCountry = (value: string | null) => {
    formik.setFieldValue('country', value);
    formik.setFieldError('country', '');

    formik.setFieldValue('abn', '');
    formik.setFieldError('abn', '');
  };

  const handleDeleteShareholder = (index: string) => {
    formik.setFieldValue('shareholders', [
      ...(formik.values.shareholders || []).filter(item => item.index !== index),
    ]);
  };

  const handleAddShareholder = () => {
    formik.setFieldValue('shareholders', [
      ...(formik.values.shareholders || []),
      { type: 'individual', first_name: '', last_name: '', index: uuidv4() },
    ]);
    formik.setFieldError('shareholders', '');
  };

  const handleDeleteOfficer = (index: string) => {
    formik.setFieldValue('officers', [
      ...(formik.values.officers || []).filter(item => item.index !== index),
    ]);
  };

  const handleAddOfficer = () => {
    formik.setFieldValue('officers', [
      ...(formik.values.officers || []),
      { type: 'individual', first_name: '', last_name: '', index: uuidv4() },
    ]);
    formik.setFieldError('officers', '');
  };

  const handleChangeSelect = (field: string, value: string | boolean) => {
    formik.setFieldValue(field, value);
    formik.setFieldError(field, '');
  };

  const getAnotherStakeholders = useCallback(
    (excludeIndex: string[]): SelectOption<string>[] => {
      const trustee: SelectOption<string>[] =
        (formik?.values?.shareholders as CompanyDetailsShareholder[])
          ?.filter(item => !excludeIndex?.includes(item?.index))
          ?.map(item => ({
            value: item.index,
            label: `Shareholder #${
              (formik?.values?.shareholders || [])?.map(item => item.index)?.indexOf(item.index) + 1
            }`,
          })) || [];
      return trustee;
    },
    [formik?.values],
  );

  const getAnotherOfficers = useCallback(
    (excludeIndex: string[]): SelectOption<string>[] => {
      const trustee: SelectOption<string>[] =
        (formik?.values?.officers as CompanyDetailsShareholder[])
          ?.filter(item => !excludeIndex?.includes(item?.index))
          ?.map(item => ({
            value: item.index,
            label: `Public Officer #${
              (formik?.values?.officers || [])?.map(item => item.index)?.indexOf(item.index) + 1
            }`,
          })) || [];
      return trustee;
    },
    [formik?.values],
  );

  useEffect(() => {
    console.log('errors', formik.errors);
  }, [formik.errors]);

  return (
    <OnboardingHolderWrapper
      formik={formik}
      onClickContinue={formik.handleSubmit}
      onClickSkip={
        allowSkip
          ? () => {
              setDirty(false);
              setTimeout(() => {
                moveForward && moveForward();
              });
            }
          : undefined
      }
      isLoading={isLoading}
      title="Association details"
    >
      <>
        <Select
          isClearable
          label="Country of Formation, Incorporation or Registration"
          options={countryOptions}
          value={formik.values.country}
          onChange={changeCountry}
          error={formik.errors.country}
        />
        <TextInputField label="Name" name="company_name" />
        {formik.values.country === 'AUS' && <TextInputField label="abn" name="abn" />}
        <TextInputField label="association number" name="association_number" />
        <TextInputField
          label="Registered Office Address"
          name="registered_office"
          placeholder="111/11 Name Street, North Sydney NSW 1111"
        />
        <TextInputField label="website (optional)" name="website" />

        <div className="w-full flex flex-col gap-6">
          <div className="flex sm:flex-row flex-col sm:items-center items-start justify-between gap-2 w-full">
            <span className="text-[16px] leading-6 font-[500] font-[Inter]">
              Tell us about Public Officers
            </span>
            <Button variant="plain" className="flex w-auto gap-1" onClick={handleAddOfficer}>
              <img src={PlusBlueIcon} alt="Loading" />
              <span className="text-[14px] leading-[22px]">Add new Public Officer</span>
            </Button>
          </div>
          {(formik.values.officers?.length ?? 0) > 0 && (
            <div className="w-full flex flex-col gap-6">
              {formik.values.officers?.map((item, index, all) => {
                const sameAs = (formik.values.shareholders || []).find(
                  shareholder => shareholder.index === item.same_as_index && item.same_as,
                );
                const itemChose = !!formik.values.shareholders?.find(
                  a => a.same_as_index === item.index,
                );
                const sameAsOfficersList = all
                  .filter(d => d.same_as_index && item.index !== d.index)
                  .map(d => d.same_as_index);
                const sameAsShareholdersList = formik.values.shareholders
                  ?.filter(
                    s =>
                      sameAsOfficersList.includes(s.index) ||
                      !!s.same_as_index ||
                      s.same_as === true ||
                      s.type !== 'individual',
                  )
                  ?.map(s => s.index);
                const anotherStakeholders = getAnotherStakeholders([
                  ...(sameAsShareholdersList ?? []),
                  item.index,
                ]);
                return (
                  <div key={'officer_' + index} className="w-full flex flex-col gap-4">
                    <div className="w-full flex sm:flex-row flex-col sm:items-center items-start justify-between gap-2">
                      <span className="font-[500] font-[Inter] text-sm leading-[22px]">
                        Public Officer #{index + 1}
                      </span>

                      <div className="flex gap-4 items-center select-none">
                        {anotherStakeholders.length > 0 && !itemChose && (
                          <span className="flex gap-4 items-center">
                            <CustomCheckbox
                              className="w-auto"
                              label={item.same_as ? 'Same as:' : 'Same as'}
                              checked={item.same_as}
                              onChange={e => {
                                if (!e.target.checked) {
                                  formik.setFieldValue(`officers[${index}].type`, 'individual');
                                  formik.setFieldError(`officers[${index}].type`, 'individual');
                                  formik.setFieldValue(`officers[${index}].same_as_index`, '');
                                }
                                handleChangeSelect(`officers[${index}].same_as`, e.target.checked);
                              }}
                            />
                            <span
                              className={classNames('text-sm leading-5 font-[Inter] w-[160px]', {
                                hidden: !item.same_as,
                              })}
                            >
                              <Select
                                options={anotherStakeholders}
                                value={item.same_as_index}
                                onChange={value => {
                                  formik.setFieldValue(`officers[${index}].type`, 'individual');
                                  formik.setFieldError(`officers[${index}].type`, '');
                                  formik.setFieldValue(`officers[${index}].first_name`, '');
                                  formik.setFieldValue(`officers[${index}].last_name`, '');
                                  formik.setFieldValue(`officers[${index}].company_name`, '');
                                  formik.setFieldError(`officers[${index}].first_name`, '');
                                  formik.setFieldError(`officers[${index}].last_name`, '');
                                  handleChangeSelect(
                                    `officers[${index}].same_as_index`,
                                    value || '',
                                  );
                                }}
                                withBorder={false}
                                showBorderBottom={false}
                                disabled={!item.same_as}
                                bgTransparent={true}
                                isClearable={true}
                              />
                            </span>
                          </span>
                        )}
                        <img
                          onClick={() => {
                            handleDeleteOfficer(item.index);
                          }}
                          className="cursor-pointer hover:opacity-80"
                          src={TrashIcon}
                          alt="Loading..."
                        />
                      </div>
                    </div>

                    <div className="w-full flex sm:flex-row flex-col sm:items-center items-start justify-between sm:gap-4 gap-2">
                      <TextInputField
                        name={`officers[${index}].first_name`}
                        value={sameAs?.first_name}
                        label="first name"
                        disabled={!!sameAs}
                      />
                      <TextInputField
                        name={`officers[${index}].last_name`}
                        value={sameAs?.last_name}
                        label="last name"
                        disabled={!!sameAs}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {typeof formik.errors?.officers === 'string' && formik.errors?.officers && (
            <span className="text-sm text-red-500">{formik.errors?.officers}</span>
          )}
        </div>

        <div className="w-full flex flex-col gap-6">
          <div className="flex sm:flex-row flex-col sm:items-center items-start justify-between gap-2 w-full">
            <span className="text-[16px] leading-6 font-[500] font-[Inter]">
              Tell us about your shareholders (optional)
            </span>
            <Button variant="plain" className="flex w-auto gap-1" onClick={handleAddShareholder}>
              <img src={PlusBlueIcon} alt="Loading" />
              <span className="text-[14px] leading-[22px]">Add new shareholder</span>
            </Button>
          </div>
          {(formik.values.shareholders?.length ?? 0) > 0 && (
            <div className="w-full flex flex-col gap-6">
              {formik.values.shareholders?.map((item, index, all) => {
                const sameAs = (formik.values.officers || []).find(
                  stakeholder => stakeholder.index === item.same_as_index && item.same_as,
                );
                const itemChose = !!formik.values.officers?.find(
                  a => a.same_as_index === item.index,
                );
                const sameAsShareholdersList = all
                  .filter(s => s.same_as_index && item.index !== s.index)
                  .map(s => s.same_as_index);
                const sameAsOfficersList = formik.values.officers
                  ?.filter(
                    d =>
                      sameAsShareholdersList.includes(d.index) ||
                      !!d.same_as_index ||
                      d.same_as === true,
                  )
                  ?.map(d => d.index);
                const anotherOfficers = getAnotherOfficers([
                  ...(sameAsOfficersList ?? []),
                  item.index,
                ]);
                return (
                  <div key={'shareholders_' + index} className="w-full flex flex-col gap-4">
                    <div className="w-full flex sm:flex-row flex-col sm:items-center items-start justify-between gap-2">
                      <span className="font-[500] font-[Inter] text-sm leading-[22px]">
                        Shareholder #{index + 1}
                      </span>
                      <div className="flex gap-4 items-center select-none">
                        {anotherOfficers.length > 0 && !itemChose && (
                          <span className="flex gap-4 items-center">
                            <CustomCheckbox
                              className="w-auto"
                              label={item.same_as ? 'Same as:' : 'Same as'}
                              checked={item.same_as}
                              onChange={e => {
                                if (!e.target.checked) {
                                  formik.setFieldValue(`shareholders[${index}].type`, 'individual');
                                  formik.setFieldError(`shareholders[${index}].type`, 'individual');
                                  formik.setFieldValue(`shareholders[${index}].same_as_index`, '');
                                }
                                handleChangeSelect(
                                  `shareholders[${index}].same_as`,
                                  e.target.checked,
                                );
                              }}
                            />
                            <span
                              className={classNames('text-sm leading-5 font-[Inter] w-[160px]', {
                                hidden: !item.same_as,
                              })}
                            >
                              <Select
                                options={anotherOfficers}
                                value={item.same_as_index}
                                onChange={value => {
                                  formik.setFieldValue(`shareholders[${index}].type`, 'individual');
                                  formik.setFieldError(`shareholders[${index}].type`, '');
                                  formik.setFieldValue(`shareholders[${index}].first_name`, '');
                                  formik.setFieldValue(`shareholders[${index}].last_name`, '');
                                  formik.setFieldValue(`shareholders[${index}].company_name`, '');
                                  formik.setFieldError(`shareholders[${index}].first_name`, '');
                                  formik.setFieldError(`shareholders[${index}].last_name`, '');
                                  formik.setFieldError(`shareholders[${index}].company_name`, '');
                                  handleChangeSelect(
                                    `shareholders[${index}].same_as_index`,
                                    value || '',
                                  );
                                }}
                                withBorder={false}
                                showBorderBottom={false}
                                disabled={!item.same_as}
                                bgTransparent={true}
                                isClearable={true}
                              />
                            </span>
                          </span>
                        )}

                        <img
                          onClick={() => {
                            formik.setFieldValue(`shareholders[${index}].first_name`, '');
                            formik.setFieldValue(`shareholders[${index}].last_name`, '');
                            formik.setFieldValue(`shareholders[${index}].company_name`, '');
                            formik.setFieldError(`shareholders[${index}].first_name`, '');
                            formik.setFieldError(`shareholders[${index}].last_name`, '');
                            formik.setFieldError(`shareholders[${index}].company_name`, '');
                            handleDeleteShareholder(item.index);
                          }}
                          className="cursor-pointer hover:opacity-80"
                          src={TrashIcon}
                          alt="Loading..."
                        />
                      </div>
                    </div>

                    <div className="w-full flex sm:flex-row flex-col sm:items-center items-start justify-between sm:gap-4 gap-2">
                      {((sameAs?.type || item.type) === 'individual' ||
                        !(sameAs?.type || item.type)) && (
                        <>
                          <TextInputField
                            label="first name"
                            name={`shareholders[${index}].first_name`}
                            value={sameAs?.first_name}
                            disabled={!!sameAs}
                          />
                          <TextInputField
                            label="last name"
                            name={`shareholders[${index}].last_name`}
                            value={sameAs?.last_name}
                            disabled={!!sameAs}
                          />
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </>
    </OnboardingHolderWrapper>
  );
};
